import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  addMonths,
  isWeekend,
} from "date-fns";
import { getBdHolidays, isHoliday, type Holiday } from "@/data/bdHolidays";

export type WeekendKind = "fri-sat" | "sat-sun" | "none";
export type DayFilter = "all" | "weekdays" | "weekends";

function isBdWeekend(d: Date, kind: WeekendKind): boolean {
  const dow = d.getDay(); // 0 Sun..6 Sat
  if (kind === "fri-sat") return dow === 5 || dow === 6;
  if (kind === "sat-sun") return dow === 0 || dow === 6;
  return false;
}

export function daysBetween(
  start: Date,
  end: Date,
  opts: { includeEnd?: boolean } = {},
): number {
  const diff = Math.abs(differenceInCalendarDays(end, start));
  return opts.includeEnd ? diff + 1 : diff;
}

export function countDays(
  start: Date,
  end: Date,
  filter: DayFilter,
  weekend: WeekendKind = "fri-sat",
  excludeHolidays = false,
): number {
  const a = start <= end ? start : end;
  const b = start <= end ? end : start;
  const holidays = excludeHolidays
    ? [...getBdHolidays(a.getFullYear()), ...getBdHolidays(b.getFullYear())]
    : [];
  let count = 0;
  const cur = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const stop = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  while (cur <= stop) {
    const we = isBdWeekend(cur, weekend);
    const ho = excludeHolidays && isHoliday(cur, holidays) !== null;
    let include = false;
    if (filter === "all") include = true;
    else if (filter === "weekdays") include = !we && !ho;
    else if (filter === "weekends") include = we;
    if (include) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

export function monthsAndDays(start: Date, end: Date) {
  const a = start <= end ? start : end;
  const b = start <= end ? end : start;
  let months = differenceInCalendarMonths(b, a);
  const after = addMonths(a, months);
  let days = differenceInCalendarDays(b, after);
  if (days < 0) {
    months -= 1;
    const after2 = addMonths(a, months);
    days = differenceInCalendarDays(b, after2);
  }
  return { months, days };
}

export function liveCountdown(target: Date, now: Date = new Date()) {
  const totalMs = target.getTime() - now.getTime();
  const future = totalMs >= 0;
  const abs = Math.abs(totalMs);
  const days = Math.floor(abs / 86_400_000);
  const hours = Math.floor((abs % 86_400_000) / 3_600_000);
  const minutes = Math.floor((abs % 3_600_000) / 60_000);
  const seconds = Math.floor((abs % 60_000) / 1000);
  return { future, days, hours, minutes, seconds, totalHours: Math.floor(abs / 3_600_000) };
}

export function upcomingHolidays(now: Date, count = 6): Holiday[] {
  const yearNow = now.getFullYear();
  const all = [...getBdHolidays(yearNow), ...getBdHolidays(yearNow + 1)];
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return all.filter((h) => new Date(h.date) >= today).slice(0, count);
}

export { isWeekend };
